import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import FileUpload from '../components/FileUpload';
import { RichTextEditor } from '../components/RichTextEditor';
import { htmlToPlainText } from '../utils/richTextUtils';
import Lottie from 'lottie-react';
import successCheckmark from '../assets/animations/success-checkmark.json';
import successStars from '../assets/animations/success-stars.json';
import successRocket from '../assets/animations/success-rocket.json';

const STORAGE_KEY = 'ticketSuccessAnimationIndex';

const animations = [
  { data: successCheckmark, label: 'Your ticket is on its way!' },
  { data: successStars, label: 'You\'re a star for reporting this!' },
  { data: successRocket, label: 'Ticket launched successfully!' },
];

function getNextAnimationIndex() {
  const stored = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
  const next = (stored + 1) % animations.length;
  localStorage.setItem(STORAGE_KEY, String(next));
  return stored;
}

const TicketForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    requestTypeId: '',
    priority: '2'
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [animationIndex, setAnimationIndex] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await apiService.getCategories();
      setCategories(data);
      if (data.length > 0) {
        setFormData(prev => ({ ...prev, categoryId: data[0].id.toString() }));
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Failed to load categories. Please try again.');
    }
  };

  const priorities = [
    { value: '1', label: 'Low' },
    { value: '2', label: 'Medium' },
    { value: '3', label: 'High' },
    { value: '4', label: 'Critical' }
  ];

  const selectedCategory = categories.find(c => c.id.toString() === formData.categoryId);
  const requestTypes = selectedCategory?.requestTypes || [];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !htmlToPlainText(formData.description) || !formData.categoryId) {
      alert('Please fill in all required fields');
      return;
    }

    if (requestTypes.length > 0 && !formData.requestTypeId) {
      alert('Please select a request type');
      return;
    }

    if (!authService.isAuthenticated()) {
      alert('You must be logged in to create a ticket');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const ticketData = {
        title: formData.title.trim(),
        description: formData.description,
        categoryId: parseInt(formData.categoryId),
        requestTypeId: formData.requestTypeId ? parseInt(formData.requestTypeId) : null,
        priority: parseInt(formData.priority)
      };

      const newTicket = await apiService.createTicket(ticketData);
      
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          for (const file of selectedFiles) {
            await apiService.uploadAttachment(newTicket.id, file);
          }
        } catch (uploadError) {
          console.error('Failed to upload attachments:', uploadError);
        } finally {
          setUploadingFiles(false);
        }
      }

      const idx = getNextAnimationIndex();
      setAnimationIndex(idx);
      setSubmitted(true);


      setTimeout(() => {
        navigate('/dashboard');
      }, 3500);
    } catch (err) {
      console.error('Failed to create ticket:', err);
      setError('Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'categoryId' ? { requestTypeId: '' } : {})
    }));
  };

  const handleDescriptionChange = (html) => {
    setFormData((prev) => ({ ...prev, description: html }));
  };

  const handleFileSelect = (file) => {
    setSelectedFiles(prev => [...prev, file]);
  };

  const handleFileRemove = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const currentUser = authService.getUserProfile();
  const userName = authService.getUserName() || 'Unknown User';

  const currentAnimation = useMemo(
    () => (animationIndex !== null ? animations[animationIndex] : null),
    [animationIndex]
  );

  if (submitted && currentAnimation) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-16 animate-fade-in">
        <div className="w-64 h-64 mb-6">
          <Lottie
            animationData={currentAnimation.data}
            loop={false}
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Created!</h2>
        <p className="text-gray-600 text-lg mb-8">{currentAnimation.label}</p>
        <p className="text-sm text-gray-400 mb-4">Redirecting to dashboard...</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          Go to Dashboard Now
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create New Ticket</h1>
        <p className="text-gray-600">Submit a new IT support request</p>
        {currentUser && (
          <p className="text-sm text-gray-500 mt-1">
            Creating ticket as: <span className="font-medium">{userName}</span>
          </p>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            required
            value={formData.title}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief description of the issue"
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <RichTextEditor
            id="description"
            value={formData.description}
            onChange={handleDescriptionChange}
            placeholder="Detailed description of the problem…"
            disabled={loading || uploadingFiles}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="categoryId"
              name="categoryId"
              required
              value={formData.categoryId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
              Priority *
            </label>
            <select
              id="priority"
              name="priority"
              required
              value={formData.priority}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {priorities.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          {requestTypes.length > 0 && (
            <div>
              <label htmlFor="requestTypeId" className="block text-sm font-medium text-gray-700 mb-2">
                Request Type *
              </label>
              <select
                id="requestTypeId"
                name="requestTypeId"
                required
                value={formData.requestTypeId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a request type</option>
                {requestTypes.map(requestType => (
                  <option key={requestType.id} value={requestType.id}>
                    {requestType.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachments (Optional)
          </label>
          <FileUpload
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            selectedFiles={selectedFiles}
            maxFiles={5}
            maxSizeMB={10}
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={loading || uploadingFiles}
          >
            {loading ? 'Creating...' : uploadingFiles ? 'Uploading Files...' : 'Create Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TicketForm; 